import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { replace } from 'react-router-redux';
import {
  Button,
  Divider,
  Container,
  Dimmer,
  Grid,
  Header,
  Icon,
  Loader,
  List,
  Message,
  Table,
} from 'semantic-ui-react';
import moment from 'moment-timezone';
import config from '../../../../config';
import { fetchM } from '../../../actions/fetchM';
import { flashMessage, flashErrorMessage } from '../../../actions/flash';
import { decodeSearch, encodeSearch } from '../../../utils/url';
import fileSize from '../../../utils/fileSize';
import isNumber from '../../../utils/numbers/isNumber';
import NavBar from '../../../components/NavBar';
import PageNav from '../../../components/PageNav';
import Timestamp from '../../../components/Timestamp';
import { namespaces, TRANSLATION_FILES } from '../../../constants/translation';

moment.locale('en-gb');

class ImportPricing extends Component {
  static propTypes = {
    t: PropTypes.func,
    token: PropTypes.string,
    id: PropTypes.string,
    loading: PropTypes.bool,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
      query: PropTypes.object,
    }),
    fetchPricingImports: PropTypes.func,
  }
  static mapStateToProps = state => ({
    appConfig: state.appConfig,
  })
  static contextTypes = {
    store: PropTypes.object,
  }
  static getPropsToState = (props) => {
    const query = decodeSearch(props.location.search);
    const count = props.count;
    const currentPage = parseInt(query.page, 10) || 1;
    const data = props.data || [];
    const limit = props.limit;
    const loading = props.loading;
    const offset = props.offset;
    const token = props.token;
    return {
      count,
      currentPage,
      data,
      limit,
      loading,
      offset,
      token,
    };
  }
  constructor(props) {
    super(props);
    const state = ImportPricing.getPropsToState(props);
    this.state = {
      ...state,
      file: null,
    };
  }
  componentWillReceiveProps(nextProps) {
    const state = ImportPricing.getPropsToState(nextProps);
    this.setState({ ...state });
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  submitFile(body) {
    const { t } = this.props;
    this.setLoading(true);
    const { store } = this.context;
    store.dispatch(fetchM(`/projects/${this.props.id}/units/import/package_pricings/`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        // Let browser decide Content-Type
        'X-Csrf-Token': this.props.token,
      },
      body,
    })).then((res) => {
      this.setLoading(false);
      res.text().then((message) => {
        const json = JSON.parse(message);
        this.setState({ file: null }, () => {
          this.props.fetchPricingImports();
          const totalCount = json.createCount + json.updateCount;
          store.dispatch(flashMessage({ header: t('Import Success', { ns: TRANSLATION_FILES.translation }), message: t('Pricing Packages Success', { ns: TRANSLATION_FILES.translation, totalCount: totalCount, createCount: json.createCount, updateCount: json.updateCount }), icon: 'checkmark', type: 'success', autoHideDuration: 10000 }));
        });
      });
    }).catch((err) => {
      this.setLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        err.res.text().then((message) => {
          store.dispatch(flashErrorMessage({ message }));
        });
        this.setState({ file: null });
        return;
      }
      store.dispatch(flashErrorMessage(err));
    });
  }
  handleAddFile = (e) => {
    e.preventDefault();
    this.import_upload.click();
  }
  handleOnChange = () => {
    const files = Array.prototype.slice.call(this.import_upload.files);
    this.import_upload.value = null;
    if (window.FileReader) {
      files.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          this.setState({ file });
        };
        reader.readAsDataURL(file);
      });
    }
  }
  handleOnSubmit = (e) => {
    e.preventDefault();
    this.handleSubmit();
  }
  handleSubmit = () => {
    const formData = new FormData();
    formData.append('import_upload', this.state.file);
    this.submitFile(formData);
  }
  handleDismiss = () => {
    this.setState({ file: null });
  }
  handleChangePage = (page) => {
    const query = decodeSearch(this.props.location.search);
    if (!query.page || !isNumber(query.page) || query.page <= 0) {
      query.page = 1;
    }
    const currentPage = parseInt(page || query.page, 10);
    query.page = currentPage;
    this.setState({ currentPage });
    this.replaceQuery(query);
  }
  replaceQuery(query) {
    const { store } = this.context;
    store.dispatch(replace(`/projects/${this.props.id}/units/import_pricings/?${encodeSearch(query)}`));
  }
  render() {
    const { t } = this.props;
    return (
      <Container>
        <NavBar
          header={t('Import pricings', { ns: TRANSLATION_FILES.translation })}
        />
        <Grid>
          <Grid.Row textAlign="center">
            <Grid.Column width={16}>
              <Dimmer
                inverted style={{ zIndex: 20 }}
                active={this.state.loading}
              >
                <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
              </Dimmer>
              <form
                encType="multipart/form-data"
                onSubmit={this.handleSubmit}
                ref={(ref) => { this.form = ref; }}
                style={{ display: 'none' }}
              >
                {t('Select file to upload:', { ns: TRANSLATION_FILES.translation })}
                <input
                  accept=".xlsx"
                  id="import_upload"
                  name="import_upload"
                  onChange={this.handleOnChange}
                  ref={(ref) => { this.import_upload = ref; }}
                  type="file"
                />
              </form>
              <Icon name="file excel outline" size="massive" color="grey" style={{ fontSize: '7em', opacity: '0.3' }} />
              <Header as="h2">
                {t('Import Pricing Packages', { ns: TRANSLATION_FILES.screens })}
                <Header.Subheader>
                  {t('Import custom unit pricing packages using xlsx.', { ns: TRANSLATION_FILES.translation })}<br />
                </Header.Subheader>
              </Header>
              <b>{t('Instructions', { ns: TRANSLATION_FILES.translation })}</b>
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'inline-block', textAlign: 'left', padding: '0.5em 0 1em' }}>
                  <List as="ol">
                    <List.Item as="li">
                      {t('Export the current pricing packages excel from', { ns: TRANSLATION_FILES.translation })}
                      <a
                        href={`${config.get('bookApiURL')}/projects/${this.props.id}/units/export/package_pricings/`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        &nbsp;{t('here', { ns: TRANSLATION_FILES.translation })}
                      </a>
                    .
                    </List.Item>
                    <List.Item as="li">{t('Edit the xlsx file and make the appropriate changes.', { ns: TRANSLATION_FILES.translation })}</List.Item>
                    <List.Item as="li">{t('Select the xlsx file and upload it back here.', { ns: TRANSLATION_FILES.translation })}</List.Item>
                  </List>
                </div>
              </div>
              {(this.state.file && this.state.file.name) ?
                <Grid centered padded>
                  <Grid.Row style={{ padding: '0 0 0.5em' }}>
                    <Grid.Column mobile={14} tablet={10} computer={7} textAlign="left">
                      <Message
                        className="import_file_selcted"
                        info
                        onDismiss={this.handleDismiss}
                        size="tiny"
                      >
                        {this.state.file.name}
                      </Message>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              :
                <Button
                  onClick={this.handleAddFile}
                  style={{ marginBottom: '0.5em' }}
                >
                  {t('Browse File', { ns: TRANSLATION_FILES.translation })}
                </Button>
              }
              <Button
                disabled={!this.state.file}
                onClick={this.handleOnSubmit}
                positive
                style={{ display: 'block', margin: 'auto' }}
              >
                {t('Upload', { ns: TRANSLATION_FILES.translation })}
              </Button>
              <Divider hidden />
              <Table
                striped unstackable
                selectable
              >
                <Table.Header fullWidth>
                  <Table.Row>
                    <Table.HeaderCell colSpan={2}>
                      <h3>{t('Imports History', { ns: TRANSLATION_FILES.translation })}</h3>
                    </Table.HeaderCell>
                    <Table.HeaderCell colSpan={2} textAlign="right">
                      <PageNav
                        currentPage={this.state.currentPage}
                        count={this.state.count}
                        limit={this.state.limit}
                        offset={this.state.offset}
                        dataSize={this.state.data.length}
                        loading={this.state.loading}
                        onChange={this.handleChangePage}
                      />
                    </Table.HeaderCell>
                  </Table.Row>
                  <Table.Row>
                    <Table.HeaderCell>{t('Name', { ns: TRANSLATION_FILES.constants })}</Table.HeaderCell>
                    <Table.HeaderCell>{t('Size', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                    <Table.HeaderCell>{t('Imported By', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                    <Table.HeaderCell>{t('Date', { ns: TRANSLATION_FILES.screens })}</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {
                    this.state.data.length > 0 ?
                      this.state.data.map((file, key) => (
                        <Table.Row key={key}>
                          <Table.Cell width={7}>
                            <a
                              href={`${config.get('bookApiURL')}/data_imports/view/${file.id}/${file.name}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Icon name="file excel outline" />
                              {file.name}
                            </a>
                          </Table.Cell>
                          <Table.Cell collapsing>
                            {fileSize(file.size)}
                          </Table.Cell>
                          <Table.Cell collapsing>
                            {file.userPreferredName}
                          </Table.Cell>
                          <Table.Cell collapsing>
                            <Timestamp format="MMMM Do YYYY, h:mm a" timestamp={file.createdAt} />
                          </Table.Cell>
                        </Table.Row>
                      ))
                    :
                      <Table.Row>
                        <Table.Cell colSpan={4} textAlign="center">
                          {t('No imports found.', { ns: TRANSLATION_FILES.translation })}
                        </Table.Cell>
                      </Table.Row>
                  }
                </Table.Body>
              </Table>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Container>
    );
  }
}

export default withTranslation(namespaces)(connect(ImportPricing.mapStateToProps)(ImportPricing));



// WEBPACK FOOTER //
// ./src/containers/Project/Units/ImportPricing.jsx